/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { getInitiativeTree, getInitiativeTypeTree } from '../../selectors/initiativeTree';
import PageStatus from '../../components/page-status';
import { ROUTES } from '../../constants/routes';
import NotAuthorised from '../not-authorised';
import * as d3 from 'd3';
import { Button } from 'reactstrap';
import { generateUrl } from '../util';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { SubsidiaryModal } from './partials/subsidiary-modal/SubsidiaryModal';
import { loadInitiativeTree } from '../../actions';
import { getRootOrg } from '../../selectors/initiative';
import './styles-folders.scss';
import { random } from 'mathjs';
import G17Client from '../../services/G17Client';
import { flextree } from 'd3-flextree'
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { BasicAlert } from '../../components/alert/BasicAlert';
import { InitiativePermissions } from '../../services/permissions/InitiativePermissions';
import { findRootParentInTree, findCurrentBranchInTree } from '../../utils/initiative';
import { MoveSubsidiaryModal } from './partials/subsidiary-modal/MoveSubsidiaryModal';
import { reloadCurrentUser } from '../../actions/user';
import { ArchiveSubsidiaryModal } from './partials/archive-subsidiary-modal/ArchiveSubsidiaryModal';
import { isStaff } from '../../selectors/user';

const DEFAULT_EXPAND_DEPTH = 0;
const ANIMATION_EXPAND_DEPTH = 4;

const REM_TO_PIXEL = 16;
const SPACING = 5;

const BUTTON_WIDTH = 30;
const MINIMUM_DISTANCE = 50;

const DROPDOWN_SIZE = {
  WIDTH: 100,
  OPTION_HEIGHT: 23,
};

const LEVELCOLOURS = [
  '#2159B5',
  '#0E9381',
  '#E7727D',
  '#C5D86D',
  '#E3B505',
  '#48BEFF',
  '#F1E8B8',
  '#A4B0F5',
  '#F2C57C'
]

function getLevelColor(depth) {
  const style = 'border-color: '
  if (LEVELCOLOURS[depth]) {
    return style + LEVELCOLOURS[depth]
  }
  return style + LEVELCOLOURS[random(0, LEVELCOLOURS.length)]
}

class InitiativeStructureFolders extends Component {

  state = {
    zoomInDisabled: false,
    zoomOutDisabled: false,
    loading: false,
    showModal: false,
    showArchiveModal: false,
    isEditModal: false,
    initiativeSelected: undefined,
    expandedNodes: [],
    firstRunAnimation: true,
    showMoveModal: false,
  }

  toggle = () => {
    this.setState({ showModal: false, isEditModal: false, initiativeSelected: undefined, showMoveModal: false });
  }

  toggleArchiveModal = () => {
    this.setState({ showArchiveModal: false, initiativeSelected: undefined });
  }

  handleUpdate = () => {
    this.props.loadInitiativeTree({
      organization: this.props.rootOrg,
      forceRefresh: true,
      blocking: false
    })
    this.props.reloadCurrentUser();
  }

  handleBackButton = (e) => {
    e.preventDefault();
    this.props.history.goBack();
  }

  handleInterestButton = async () => {
    const formData = {
      title: 'I am interested in the organisation map',
      message: 'I am interested in the organisation map',
      company: this.props.initiative.data.name,
    };

    this.setState({ loading: true });

    return G17Client.userInterest(formData)
      .then(() => {
        this.setState({ loading: false });
        this.props.addSiteAlert({
          content: 'Thanks. We will be in contact with more information soon.',
          timeout: 5000,
          color: SiteAlertColors.Success,
        });
      })
      .catch((e) => {
        this.setState({ loading: false });
        this.props.addSiteAlert({
          content: 'There was a problem sending the request. Please try again.',
          color: SiteAlertColors.Danger,
        });
      });
  };

  isReportingLevelsAllowed = () => {
    const { isPortfolioTracker } = this.props;
    if (isPortfolioTracker) {
      return true;
    }
    return this.props.limitReportingLevels > 1;
  }

  canAddMoreLevels = () => {
    const { initiativeTreeList, limitReportingLevels, isPortfolioTracker, canManage } = this.props;
    if (!canManage || isPortfolioTracker) {
      return false;
    }

    return initiativeTreeList.length < limitReportingLevels;
  }

  canEditLevels = () => {
    const { isPortfolioTracker, canManage } = this.props;
    if (this.props.limitReportingLevels <= 1 || !canManage || isPortfolioTracker) {
      return false;
    }
    return true;
  }

  onMapClick = (event) => {
    if (event.target.matches('#map_dropdown') || event.target.matches('#map_dropdown i')) {
      return;
    }
    this.onCloseDropdownMenu();
  };

  onCloseDropdownMenu = () => {
    const dropdowns = document.getElementsByClassName('node_dropdown_menu');
    for (const dropdown of dropdowns) {
      dropdown.remove();
    }
  };

  renderTopMessage = () => {
    const { initiativeTreeList, limitReportingLevels, isPortfolioTracker, canManage } = this.props;

    if (!canManage || isPortfolioTracker) {
      return null;
    }

    if (!this.isReportingLevelsAllowed()) {
      return (
        <div className='contact-us__wrapper'>
          <p className='text-center text-md'>Organisation map</p>
          <Button
            color='primary'
            className='d-flex align-items-center'
            onClick={() => this.handleInterestButton()}
            disabled={this.state.loading}
          >
            {this.state.loading ? (
              <div className='spinner-border spinner-border-sm text-secondary mr-2' role='status'></div>
            ) : null}
            Contact us to find out more
          </Button>
        </div>
      );
    }

    if (!this.canAddMoreLevels()) {
      return (
        <div className='subscription-warning-wrapper text-lg'>
          <BasicAlert type='danger'>You have reached your subsidiary limit ({initiativeTreeList.length} of {limitReportingLevels})</BasicAlert>
        </div>
      );
    }

    const isUnlimited = limitReportingLevels >= 999;

    return (
      <div className='subscription-info-wrapper text-lg'>
        <BasicAlert type='info'>You currently have {initiativeTreeList.length} subsidiaries. {isUnlimited ? '' : `(Limit: ${limitReportingLevels})`}</BasicAlert>
      </div>
    );
  }

  render() {
    const { hasErrored, loading, initiativeTree, initiativeTreeList } = this.props;
    const { zoomInDisabled, zoomOutDisabled, showModal, isEditModal, showMoveModal, showArchiveModal } = this.state;

    if (this.props.showPermissionDenied) {
      return <NotAuthorised />
    }

    return (
      <PageStatus error={hasErrored || initiativeTree.children === 0} loading={loading}>
        <SubsidiaryModal
          isOpen={showModal}
          isEdit={isEditModal}
          initiativeSelected={this.state.initiativeSelected}
          toggle={this.toggle}
          handleUpdate={this.handleUpdate}
        />
        <MoveSubsidiaryModal
          isOpen={showMoveModal}
          initiativeId={this.state.initiativeSelected?.initiativeId}
          initiativeTree={initiativeTree}
          initiativeTreeList={initiativeTreeList}
          toggle={this.toggle}
          handleUpdate={this.handleUpdate}
        />
        <ArchiveSubsidiaryModal
          isOpen={showArchiveModal}
          selectedInitiative={this.state.initiativeSelected}
          initiativeTreeList={initiativeTreeList}
          toggle={this.toggleArchiveModal}
          handleUpdate={this.handleUpdate}
        />
        <div className='mindmap-wrapper'>
          <div className='backButton'>
            <Button
              type='button'
              color='link'
              onClick={this.handleBackButton}
            >
              <i className='fas fa-arrow-left mr-2'></i>
              Back
            </Button>
          </div>

          {this.renderTopMessage()}

          <div className='mindmap-menu row'>
            <div className='col col-auto'>
              <button disabled={zoomInDisabled} onClick={() => this.handleZoom(2)}
                className='fa-light fa-magnifying-glass-plus'></button>
              <button disabled={zoomOutDisabled} onClick={() => this.handleZoom(0.5)}
                className='fa-light fa-magnifying-glass-minus'></button>
              Zoom
            </div>

            <div className='col col-auto'>
              <button onClick={() => this.handleReset()}>
                Reset
                <span className='fa-light fa-expand'></span>
              </button>
            </div>
          </div>
          <div ref={element => this.initiativeStructure = element} className='mindmap  dont_translate' onClick={this.onMapClick}></div>
          {!this.isReportingLevelsAllowed() ? (
            <div className='details text-center'>
              This organisation map allows you to gather data from business subsidiaries and divisions and aggregate
              the data up to head office level
            </div>
          ) : null}
        </div>
      </PageStatus>
    )
  }

  componentDidMount() {
    const { initiativeTree, initiative, isPortfolioTracker, match } = this.props;
    if (initiativeTree.loaded) {
      const treeData = this.getTreeFilterRoot(initiativeTree);
      const id = isPortfolioTracker ? match.params.portfolioId : initiative.data?._id;
      const currentBranch = findCurrentBranchInTree(treeData, id).branch;
      const expandedIds = currentBranch.filter((node) => node.id !== id).map((node) => node.id);
      this.setState({ expandedNodes: expandedIds }, this.getInitiativeStructure);
    }
  }

  componentDidUpdate(prevProps) {
    if ((!prevProps.initiativeTree.loaded && this.props.initiativeTree.loaded)) {
      this.getInitiativeStructure();
    }
    else if (prevProps.initiativeTree.children !== this.props.initiativeTree.children) {
      //remove the tree and rerender with updated data
      d3.select('.mindmap').html('')
      this.getInitiativeStructure();
    }
  }

  getTreeFilterRoot() {
    const {isPortfolioTracker, rootOrg, initiativeTree, match } = this.props;
    const defaultTreeRoot = initiativeTree.children[0];

    if (isPortfolioTracker) {
      const portfolioId = match.params.portfolioId;
      if (portfolioId) {
        const rootInitiative = findRootParentInTree(initiativeTree, portfolioId);
        return rootInitiative ?? defaultTreeRoot;
      }
    }

    if (rootOrg) {
      const foundTreeRoot = initiativeTree.children.find((initiative) => rootOrg?._id === initiative.id);
      return foundTreeRoot ?? defaultTreeRoot;
    }

    return defaultTreeRoot;
  }

  getInitiativeStructure() {
    const { history, initiativeTree, onClick } = this.props;

    const canEdit = this.canEditLevels();
    const canAdd = this.canAddMoreLevels();
    const showOrgMapAnimation = !this.isReportingLevelsAllowed();

    const onNodeClick = onClick ? onClick : (initiativeId) => {
      history.push(generateUrl(ROUTES.SUMMARY, { initiativeId }))
    }

    if (initiativeTree.children.length === 0) {
      return;
    }

    const treeFilteredRoot = this.getTreeFilterRoot();

    // ************** Generate the tree diagram	 *****************
    const width = this.initiativeStructure.offsetWidth
    const height = this.initiativeStructure.offsetHeight
    const duration = 250
    const maxLength = 40;
    const nodeHeight = '3rem'
    const heightCenterOffset = 20
    const getNodeHtmlWidth = (d) => {
      // styled wrapper not the svg node width
      const namelength = d.data.name.length
      let factor = 4
      if (namelength > 7 && namelength <= 15) {
        factor = 3
      } else if (namelength > 15) {
        factor = 2.5
      }
      return `${Math.max(Math.min(namelength / 2 + factor, 20), 8)}rem`
    }
    // flextree functionality to get variable node size
    const getNodeSize = (node) => {
      // Minimum 5, to ensure we have enough space between the nodes
      const nodeNameLength = Math.max(Math.min(node.data.name.length, maxLength), 5);
      if (nodeNameLength > 20) {
        return [70, nodeNameLength * 15]
      }
      if (nodeNameLength > 10) {
        return [70, nodeNameLength * 20]
      }
      return [70, nodeNameLength * 50]
    }
    //zoom
    const initialScale = showOrgMapAnimation ? .8 : 1
    const scaleExtent = [.2, 2]
    const defaultTranslateX = showOrgMapAnimation ? width / 4 : 100;

    let idCounter = 0;

    const treeData = showOrgMapAnimation ? getCTLightPreviewOrgMap(treeFilteredRoot) : treeFilteredRoot;

    const root = { ...treeData, x0: height / 2, y0: 0 };

    // create a hierarchy from the root
    // flextree allows for more compact tree since nodes have variable sizes
    const treemap = flextree().nodeSize(getNodeSize)

    const treeRoot = d3.hierarchy(root, d => d.children)
    treeRoot.x0 = height / 2;
    treeRoot.y0 = 0;

    const zoomed = (event, d) => {
      const currentEvent = event.transform;
      svg.attr('transform', currentEvent);

      const currentScale = currentEvent.k
      const [min, max] = scaleExtent;
      // limit translation to thresholds
      if (currentScale <= min) {
        this.setState({ zoomOutDisabled: true });
      } else if (currentScale >= max) {
        this.setState({ zoomInDisabled: true });
      } else {
        this.setState({ zoomInDisabled: false, zoomOutDisabled: false });
      }
    }

    const actualSvg = d3.select(this.initiativeStructure)
      .append('svg').attr('width', width).attr('height', height)

    const svg = actualSvg.append('g');

    const zoom = d3.zoom().scaleExtent(scaleExtent).on('zoom', zoomed);

    actualSvg.call(zoom.transform, d3.zoomIdentity.translate(defaultTranslateX, height / 2).scale(initialScale));

    actualSvg.call(zoom);

    treemap(treeRoot);

    const collapse = (d, depth = 0) => {
      if (d.children) {
        depth++;
        d._children = d.children;
        d.children.forEach((e) => collapse(e, depth));
        if (depth > (showOrgMapAnimation ? ANIMATION_EXPAND_DEPTH : DEFAULT_EXPAND_DEPTH)) {
          if (!this.state.expandedNodes.includes(d.data.id)) {
            d.children = null;
          }
        }
      }
    }

    if (treeRoot.children) {
      treeRoot.children.forEach(collapse);
    }

    const unsetAnimation = () => {
      this.setState({ firstRunAnimation: false });
    }

    // add opacity animation for ESGenome and Company Tracker Standard

    const runOrgMapAnimation  = (nodeEnter, linkEnter) => {
      if (this.state?.firstRunAnimation && showOrgMapAnimation) {
        ['.mindmap-text-wrap', '.expand-line', '.expand-container'].forEach((className, index) => {
          nodeEnter.select(className).style('opacity', 0);
          nodeEnter
            .select(className)
            .style('animation', (node) => `nodeAnimation ${index + 0.2}s ${node.depth * 0.8}s forwards`);
        });
        linkEnter.style('opacity', 0);
        linkEnter.style('animation', (node) => `linkAnimation 0.2s ${node.depth * 0.8}s forwards`);
        unsetAnimation();
      }
    }

    update(treeRoot);

    this.handleReset = function () {
      actualSvg
        .transition()
        .duration(750)
        .call(zoom.transform, d3.zoomIdentity.translate(defaultTranslateX, height / 2).scale(initialScale));
    }

    this.handleZoom = function (amount) {
      actualSvg.transition().call(zoom.scaleBy, amount)
    }
    const handleEdit = (initiativeId, name) => {
      this.setState({ showModal: true, isEditModal: true, initiativeSelected: { initiativeId, name } })
    }
    const handleAdd = (initiativeId) => {
      this.setState({ showModal: true, initiativeSelected: { initiativeId } })
    }

    const handleMoveSubsidiary = (initiativeId) => {
      this.setState({ showMoveModal: true, initiativeSelected: { initiativeId } })
    }

    const handleArchive = (initiativeId, name) => {
      this.setState({ showArchiveModal: true, initiativeSelected: { initiativeId, name } })
    }

    const addToExpandedNodes = (node) => {
      this.setState(state => ({expandedNodes: [...state.expandedNodes, node]}))
    }

    const removeFromExpandedNodes = (node) => {
      this.setState(state => ({expandedNodes: [...state.expandedNodes.filter(n => n !== node)]}))
    }

    function update(source) {
      // Assigns the x and y position for the nodes
      const treeData = treemap(treeRoot)
      // Compute the new tree layout.
      const nodes = treeData.descendants();
      const links = treeData.descendants().slice(1);
      // Update the nodes…
      const node = svg.selectAll('g.node').data(nodes, d => d.id || (d.id = ++idCounter));

      // Enter any new nodes at the parent's previous position.
      const nodeEnter = node.enter().append('g')
        .attr('class', 'node')
        .attr('transform', `translate(${source.y0},${source.x0})`)

      // Add a connecting line between node & expand button
      drawConnectingLineToExpandButton(nodeEnter);

      // Create an expand/collapse button
      drawExpandButton(nodeEnter)

      nodeEnter.append('svg:foreignObject')
        .attr('height', nodeHeight)
        .attr('width', getNodeHtmlWidth)
        .attr('class', function (d) {
          return 'shadow-lg mindmap-text-wrap rounded-1'
        })
        .attr('style', function (d) {
          return getLevelColor(d.depth)
        })
        .html(function (d) {
          const name = d.data.name
          const ellipsisName = name.length > maxLength ? name.substring(0, maxLength - 3) + '...' : name
          const actionButtons = '<button id="map_dropdown" class="btn-link py-1 px-2 mr-2"><i class=\'fas fa-chevron-down\' id="map_dropdown_icon"></i></button>';
          const mainClass = (!d.data.disabled) ? 'mindmap-text text-ThemeTextMedium' : 'text-ThemeTextPlaceholder mindmap-disabled-text'
          return `<div class="${mainClass} name d-flex align-items-center justify-content-between h-100 gap-1">
        <div class='flex-grow-1 h-100'>
        <div class='d-flex w-100 h-100 align-items-center text-md' id='initiativename'>${ellipsisName}</div>
        </div>
        ${!d.data.id || d.data.disabled ? '' : `<div class="d-flex flex-column">${actionButtons}</div>`}
        </div>`
        })
        .on('click', function (event, d) {
          if (!d.data.id || d.data.disabled) {
            return
          }
          const [initiativeId] = d.data.id.split('_');
          if (event.target.id === 'initiativename') {
            onNodeClick(initiativeId)
          }
          if (event.target.id === 'map_dropdown' || event.target.id === 'map_dropdown_icon') {
            openDropdownMenu(d);
          }
        })
        .append('title').text(d => d.data.name);

      // UPDATE
      const nodeUpdate = nodeEnter.merge(node)

      // Transition to the proper position for the node
      nodeUpdate.transition()
        .duration(duration)
        .attr('transform', function (d) {
          return `translate(${d.y}, ${d.x - heightCenterOffset})`;
        });

      // Transition exiting nodes to the parent's new position.
      const nodeExit = node.exit().transition()
        .duration(duration)
        .attr('transform', d => `translate(${source.y},${source.x})`)
        .remove()

      // On exit reduce the opacity of text labels
      nodeExit.selectAll('text').style('fill-opacity', 0);

      // ****************** links section ***************************

      // Update the links...
      const link = svg.selectAll('path.link').data(links, d => d.id)

      // Enter any new links at the parent's previous position.
      const time = Date.now()
      const linkEnter = link.enter().insert('path', 'g')
        .attr('class', 'link ' + time)
        .attr('d', () => {
          const o = { x: source.x0, y: source.y0, data: source.data }
          return drawStepPath(o, o)
        })

      runOrgMapAnimation(nodeEnter, linkEnter);

      // UPDATE
      const linkUpdate = linkEnter.merge(link)

      // Transition back to the parent element position
      linkUpdate.transition()
        .duration(duration)
        .attr('d', d => drawStepPath(d, d.parent));

      // Remove any exiting links
      link.exit().transition()
        .duration(duration)
        .attr('d', () => {
          const o = { x: source.x, y: source.y, data: source.data }
          return drawStepPath(o, o)
        })
        .remove()

      // Store the old positions for transition.
      nodes.forEach(function (d) {
        d.x0 = d.x;
        d.y0 = d.y;
      });


      function onExpandButtonClick(event, d) {
        const parentSelect = d3.select(this.parentElement);
        const button = parentSelect.select('.expand-button');
        const line = parentSelect.select('.expand-line');
        line.style('opacity', '25%');
        line.style('animation', 'none');
        if (d.children) {
          d._children = d.children;
          d.children = null;
          button.html('<i class=\'fal fa-plus\'></i>');
          line.style('display', 'inline-block')
          removeFromExpandedNodes(d.data.id);
        } else {
          button.html('<i class=\'fal fa-minus\'></i>');
          d.children = d._children;
          d._children = null;
          line.style('display', 'none')
          addToExpandedNodes(d.data.id);
        }

        // Update current node container
        parentSelect.select('.expand-container')
          .attr('transform', (d) => `translate(${getExpandButtonDistance(d)}, 10)`);
        line.attr('width', (d) => {
          return getExpandButtonDistance(d);
        })

        update(d);
      }

      function expandedNodePositionShift(parent) {
        const length = parent.data?.name?.length ?? 3;
        // For small nodes, we want to expand at least by node width
        const adjustment = length <= 20 ? BUTTON_WIDTH : 40;
        return parent.children?.length ? adjustment : Math.min(length, 50);
      }

      function drawStepPath(child, parent) {
        const deltaY = parent.y - child.y;
        const extra = expandedNodePositionShift(parent);
        return `M${child.y}, ${child.x} H${child.y + 50 + extra + deltaY / 2} V${parent.x} H${parent.y}`
      }

      function getExpandButtonDistance(d) {
        const firstChildNode = d.children ? d.children[0] : d._children ? d._children[0] : d;
        const source = { x: firstChildNode.x, y: firstChildNode.y };
        const target = { x: d.x, y: d.y };
        const extra = expandedNodePositionShift(d);
        return 0.5 * (source.y - target.y) + MINIMUM_DISTANCE + extra - BUTTON_WIDTH / 2;
      }

      function drawConnectingLineToExpandButton(nodeEnter) {
        nodeEnter
          .append('svg:foreignObject')
          .attr('height', 2)
          .attr('width', (d) => {
            return getExpandButtonDistance(d);
          })
          .style('display', (d) => {
            return d.children || d._children ? 'inline-block' : 'none';
          })
          .attr('class', 'expand-line');
      }

      function drawExpandButton(nodeEnter) {
        nodeEnter
          .append('svg:foreignObject')
          .attr('width', BUTTON_WIDTH)
          .attr('height', BUTTON_WIDTH)
          .attr('class', 'expand-container')
          .attr('transform', (d) => {
            return `translate(${getExpandButtonDistance(d)}, 10)`;
          })
          .html(function (d) {
            if (d.children && d.children.length) {
              const expandButton = '<button class=\'expand-button\'><i class=\'fal fa-minus\'></i></button>';
              return `<div class='wrapper'>${expandButton}</div>`;
            }
            if (d._children && d._children.length) {
              const expandButton = '<button class=\'expand-button\'><i class=\'fal fa-plus\'></i></button>';
              return `<div class='wrapper'>${expandButton}</div>`;
            }
            return;
          })
          .on('click', onExpandButtonClick);
      }
    }

    const openDropdownMenu = (d) => {
      const dropdown = d3.select('.node_dropdown_menu');
      if (dropdown.size()) {
        return dropdown.remove();
      }
      const [initiativeId] = d.data.id.split('_');
      const x = nodeHeight.replace('rem', '') * REM_TO_PIXEL + d.x - BUTTON_WIDTH;
      const y = getNodeHtmlWidth(d).replace('rem', '') * REM_TO_PIXEL + d.y - BUTTON_WIDTH - SPACING;

      const editButton = canEdit
        ? '<li id="map_edit" class="px-2 py-1"><i class=\'mr-2 fal fa-pencil\'></i>Rename</li>'
        : '';
      const addButton = canAdd
        ? '<li id="map_add" class="px-2 py-1"><i class=\'mr-2 fal fa-plus\'></i>Create child</li>'
        : '';
      const moveButton = canEdit
        ? '<li id="map_move" class="px-2 py-1"><i class="mr-2 fal fa-arrows-turn-to-dots"></i>Move</li>'
        : '';
      const archiveButton =
        this.props.rootOrg?._id !== initiativeId && canEdit && this.props.isStaff
          ? '<li id="maparchive" class="px-2 py-1"><i class="mr-2 fal fa-trash-undo"></i>Archive</li>'
          : '';

      const options = [addButton, editButton, moveButton, archiveButton].filter((option) => option);

      if (!options.length) {
        return;
      }

      svg
        .append('g')
        .attr('class', 'node_dropdown_menu')
        .attr('transform', `translate(${y},${x})`)
        .append('svg:foreignObject')
        .attr('height', DROPDOWN_SIZE.OPTION_HEIGHT * options.length + SPACING * 2)
        .attr('width', DROPDOWN_SIZE.WIDTH)
        .attr('class', 'dropdown_menu')
        .html(function (d) {
          return `<div><ul class='px-0 py-1'>${options.join('')}</ul></div>`;
        })
        .on('click', function (event) {
          if (event.target.id === 'map_edit') {
            handleEdit(initiativeId, d.data.name);
            return;
          }
          if (event.target.id === 'map_add') {
            handleAdd(initiativeId);
            addToExpandedNodes(d.data.id);
            return;
          }
          if (event.target.id === 'map_move') {
            handleMoveSubsidiary(initiativeId);
            return;
          }
          if (event.target.id === 'maparchive') {
            handleArchive(initiativeId, d.data.name);
            return;
          }
        });
    };
  }
}

const mapStateToProps = (state, ownProps) => ({
  initiativeTree: ownProps.customTree ?? getInitiativeTree(state),
  showPermissionDenied: state.authentication.showPermissionDenied,
  currentUser: state.currentUser,
  initiative: state.initiative,
  limitReportingLevels: FeaturePermissions.getLimitReportingLevels(state),
  initiativeTreeList: getInitiativeTypeTree(state),
  rootOrg: getRootOrg(state),
  canManage: state.initiative.data ? InitiativePermissions.canManageInitiative(state.currentUser.data, state.initiative.data._id) : false,
  isStaff: isStaff(state),
});

const mapDispatchToProps = {
  addSiteAlert,
  loadInitiativeTree,
  reloadCurrentUser,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InitiativeStructureFolders));

const getCTLightPreviewOrgMap = (company) => {
  return {
    name: company.name,
    id: company._id,
    children: [
      {
        name: 'Business Unit 1',
        children: [
          {
            name: 'Americas',
            children: [],
          },
          {
            name: 'ASEAN',
            children: [
              {
                name: 'Subsidiary 1',
                children: [
                  {
                    name: 'Site/Branch/Asset 1',
                    children: [],
                  },
                  {
                    name: 'Site/Branch/Asset 2',
                    children: [],
                  },
                  {
                    name: 'Site/Branch/Asset 3',
                    children: [],
                  },
                ],
              },
              {
                name: 'Subsidiary 2',
                children: [],
              },
              {
                name: 'Subsidiary 3',
                children: [],
              },
            ],
          },
          {
            name: 'APAC',
            children: [],
          },
        ],
      },
      {
        name: 'Business Unit 2',
        children: [],
      },
      {
        name: 'Business Unit 3',
        children: [],
      },
    ],
  };
};
